/**
 * Sanitize HTML-bearing fields before they leave the client and enter the LLM
 * prompt or query logs.
 *
 * Ticket/email content from Zoho Desk (and similar CRMs) arrives as
 * Gmail-formatted HTML with inline styles, tracking pixels, and nested
 * quoted-reply history. Stripping it on the way out keeps:
 *   - request payloads small (HTML can be 10-50x the prompt size)
 *   - DevTools Network requests readable (the original motivation)
 *   - the LLM focused on the actual message
 *
 * Mirrors `functions/sms/sanitize-prompt.js` byte-for-byte semantics. Both
 * layers run: the client sanitizes for payload hygiene; the server sanitizes
 * as the authoritative boundary (defense-in-depth — never trust the client).
 *
 * This is NOT an HTML sanitizer for rendering untrusted markup in the DOM.
 * Never feed the output back into `dangerouslySetInnerHTML`.
 */

const DEFAULT_MAX_INPUT = 200_000;
const DEFAULT_MAX_OUTPUT = 50_000;

const HTML_DETECT_RE = /<\/?[a-z][^<>]*>|<!--/i;
const COMMENT_RE = /<!--[\s\S]*?-->/g;
const DANGEROUS_BLOCK_RE =
    /<(script|style|iframe|object|embed|noscript|template)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;
const DANGEROUS_SELF_RE =
    /<(script|style|iframe|object|embed|noscript|template)\b[^>]*\/?\s*>/gi;
const BLOCKQUOTE_RE = /<blockquote\b[^>]*>[\s\S]*?<\/blockquote\s*>/gi;
const BR_RE = /<br\s*\/?\s*>/gi;
const BLOCK_CLOSE_RE = /<\/(p|div|li|tr|h[1-6])\s*>/gi;
const ANY_TAG_RE = /<\/?[a-zA-Z][^<>]*>/g;

// Minimal entity decode covering what email/HTML clients actually emit. We
// intentionally avoid pulling in a full entity library — payload size matters
// on the client. The decode runs AFTER tag stripping so an entity-encoded tag
// (`&lt;script&gt;`) shows up as visible text, not re-introduced markup.
const NAMED_ENTITIES: Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
    copy: '©',
    reg: '®',
    hellip: '…',
    mdash: '—',
    ndash: '–',
    lsquo: '‘',
    rsquo: '’',
    ldquo: '“',
    rdquo: '”',
    bull: '•',
};

function decodeEntities(s: string): string {
    return s.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (full, raw: string) => {
        if (raw[0] === '#') {
            const isHex = raw[1] === 'x' || raw[1] === 'X';
            const code = parseInt(raw.slice(isHex ? 2 : 1), isHex ? 16 : 10);
            if (Number.isFinite(code) && code >= 0 && code <= 0x10ffff) {
                try {
                    return String.fromCodePoint(code);
                } catch {
                    return full;
                }
            }
            return full;
        }
        const named = NAMED_ENTITIES[raw.toLowerCase()];
        return named !== undefined ? named : full;
    });
}

function looksLikeHTML(s: string): boolean {
    return typeof s === 'string' && HTML_DETECT_RE.test(s);
}

export interface SanitizeOptions {
    /** Drop <blockquote> contents (Gmail-style quoted reply history). Default true. */
    dropQuotedReplies?: boolean;
    /** Byte cap on raw input (defense-in-depth). Default 200_000. */
    maxInput?: number;
    /** Byte cap on output. Default 50_000. */
    maxOutput?: number;
}

/**
 * Convert HTML-bearing text into prompt-safe plain text. Pass-through for
 * strings that don't contain markup (cheap heuristic check first).
 */
export function sanitizeHTMLForPrompt(
    input: unknown,
    opts: SanitizeOptions = {}
): string {
    if (typeof input !== 'string' || input.length === 0) return '';
    if (!looksLikeHTML(input)) return input;

    const maxInput = opts.maxInput ?? DEFAULT_MAX_INPUT;
    const maxOutput = opts.maxOutput ?? DEFAULT_MAX_OUTPUT;
    const dropQuoted = opts.dropQuotedReplies !== false;

    let s = input.length > maxInput ? input.slice(0, maxInput) : input;

    s = s.replace(COMMENT_RE, ' ');
    s = s.replace(DANGEROUS_BLOCK_RE, ' ');
    s = s.replace(DANGEROUS_SELF_RE, ' ');
    if (dropQuoted) s = s.replace(BLOCKQUOTE_RE, ' ');

    s = s.replace(BR_RE, '\n');
    s = s.replace(BLOCK_CLOSE_RE, '\n');

    s = s.replace(ANY_TAG_RE, '');
    s = decodeEntities(s);

    s = s.replace(/[ \t]+/g, ' ');
    s = s.replace(/\n[ \t]+/g, '\n');
    s = s.replace(/\n{3,}/g, '\n\n');
    s = s.trim();

    if (s.length > maxOutput) s = s.slice(0, maxOutput);
    return s;
}

/**
 * Internal — exposed for testing only.
 */
export const _internal = { looksLikeHTML, decodeEntities };
